import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { errorMessage, isUnreachableError } from "@/lib/errors";
import {
  asUser,
  checkServerReachable,
  currentUser,
  pb,
  refreshSession,
  renewFileToken,
  signInWithPassword,
  signOut as clearSession,
  type UsersResponse,
} from "@/lib/pb";
import { consentIdFromPath } from "@/features/oauth-consent/model";
import { SessionContext, type Session, type SessionStatus } from "./SessionContext";

/** How often a live session re-mints its token. */
const REFRESH_INTERVAL_MS = 30 * 60_000;

/**
 * Owns the session state machine and keeps the PocketBase token alive.
 *
 * The distinction that matters here: a failed reachability check produces
 * `unreachable`, never `signed-out`. Signing the user out because the VPS
 * blinked would make an outage look like a logout, and an empty screen look
 * like an empty day.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SessionStatus>("connecting");
  const [user, setUser] = useState<UsersResponse | null>(currentUser);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const lastRefreshRef = useRef(0);

  /** Reachability check followed by a token refresh. */
  const connect = useCallback(async () => {
    setStatus((current) => (current === "signed-in" ? current : "connecting"));
    setConnectionError(null);

    try {
      await checkServerReachable();
    } catch (error) {
      setConnectionError(
        isUnreachableError(error)
          ? "The server did not respond."
          : errorMessage(error),
      );
      setStatus("unreachable");
      return;
    }

    const valid = await refreshSession();
    if (valid) await renewFileToken();
    lastRefreshRef.current = Date.now();
    setStatus(valid ? "signed-in" : "signed-out");
  }, []);

  useEffect(() => {
    void connect();
  }, [connect]);

  // The auth store is the source of truth: a 401 handled deep inside a query,
  // or a sign-out in another tab, must move the whole app back to the login
  // screen without anyone having to remember to call setStatus.
  useEffect(() => {
    return pb.authStore.onChange((_token, record) => {
      setUser(asUser(record));
      setStatus((current) => {
        if (record) {
          return "signed-in";
        }
        return current === "unreachable" ? current : "signed-out";
      });
      if (!record) {
        queryClient.clear();
        // The route can name the last account's project and carry its search words.
        // An OAuth consent link names no account's data, and must survive signing in.
        const hash = window.location.hash;
        if (hash && hash !== "#/" && !consentIdFromPath(hash.slice(1))) window.location.hash = "#/";
      }
    }, true);
  }, [queryClient]);

  // Keep the token fresh while the app is open, and top it up whenever the
  // window is brought back after a long spell in the background.
  useEffect(() => {
    if (status !== "signed-in") {
      return;
    }

    const refresh = async () => {
      if (Date.now() - lastRefreshRef.current < REFRESH_INTERVAL_MS) {
        return;
      }
      lastRefreshRef.current = Date.now();
      await refreshSession();
      await renewFileToken();
    };

    const interval = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const record = await signInWithPassword(email, password);
      await renewFileToken();
      lastRefreshRef.current = Date.now();
      setUser(record);
      setConnectionError(null);
      setStatus("signed-in");
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const signOut = useCallback(() => {
    clearSession();
    queryClient.clear();
    setUser(null);
    setStatus("signed-out");
  }, [queryClient]);

  const value = useMemo<Session>(
    () => ({ status, user, connectionError, signIn, signOut, reconnect: connect }),
    [status, user, connectionError, signIn, signOut, connect],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
